import { type AggRequest, type AggResponse, fetchAgg } from '../../../../common/utils/fetch-agg';
import { generateHash } from '../../../../utils/caching/common/key-generator';
import {
	type CacheAnalyticsCallback,
	type NetworkCallbacks,
	promiseCache,
} from '../../../../utils/caching/persist-refresh';
import { extractTraceInfoFromResponse } from '../../../../utils/network-trace-info';

import { QUICK_FIND_RECENT_ACTIVITIES_CACHE_KEY } from './constants';
import { operationName, quickFindRecentActivitiesQuery } from './query';
import {
	type JiraIssueResponse,
	type JiraIssuesQueryVariables,
	type QuickFindRecentActivitiesQuery,
	type QuickFindRecentActivitiesQueryVariables,
} from './types';

export type QuickFindRecentActivitiesQueryProps = Pick<
	AggRequest<QuickFindRecentActivitiesQueryVariables>,
	'aggAbsoluteUrl' | 'variables' | 'headers'
> & {
	networkCallbacks?: NetworkCallbacks;
};

export const recentActivitiesQuery: ({ variables, aggAbsoluteUrl, headers, networkCallbacks, }: QuickFindRecentActivitiesQueryProps) => Promise<AggResponse<QuickFindRecentActivitiesQuery>> = ({
	variables,
	aggAbsoluteUrl,
	headers,
	networkCallbacks,
}: QuickFindRecentActivitiesQueryProps) => {
	networkCallbacks?.onStart();
	return fetchAgg<QuickFindRecentActivitiesQueryVariables, QuickFindRecentActivitiesQuery>({
		variables,
		aggAbsoluteUrl,
		graphQuery: quickFindRecentActivitiesQuery,
		operationName,
		headers,
	}).then(
		(result) => {
			const traceInfo = extractTraceInfoFromResponse(result);
			networkCallbacks?.onSuccess(traceInfo);
			return result;
		},
		(error) => {
			networkCallbacks?.onError(
				error?.name || 'UnknownError',
				error?.message || 'An unknown error occurred',
			);
			throw error;
		},
	);
};

const {
	refreshOnAccessDecorator: refreshOnAccessDecoratorRecentActivities,
	clear: clearRecentActivitiesCache,
} = promiseCache<AggResponse<QuickFindRecentActivitiesQuery>>({
	name: QUICK_FIND_RECENT_ACTIVITIES_CACHE_KEY,
});

type CachedRecentActivitiesQueryProps = QuickFindRecentActivitiesQueryProps & {
	expiration?: number | undefined;
	onlyRefreshIfExpired?: boolean | undefined;
	returnExpired?: boolean | undefined;
	analyticsCallback?: CacheAnalyticsCallback | undefined;
	networkCallbacks?: NetworkCallbacks;
};

const FAILURE_EXPIRATION_MS = 5 * 1000; // 5 seconds for failure
const SUCCESS_EXPIRATION_MS = 5 * 60 * 1000; // 5 minutes for success

export const cachedRecentActivitiesQuery: ({ variables, aggAbsoluteUrl, headers, expiration, onlyRefreshIfExpired, returnExpired, analyticsCallback, networkCallbacks, }: CachedRecentActivitiesQueryProps) => AggResponse<QuickFindRecentActivitiesQuery> | Promise<AggResponse<QuickFindRecentActivitiesQuery>> = ({
	variables,
	aggAbsoluteUrl,
	headers,
	expiration,
	onlyRefreshIfExpired,
	returnExpired,
	analyticsCallback,
	networkCallbacks,
}: CachedRecentActivitiesQueryProps) => {
	const cacheKey = generateHash(
		`${QUICK_FIND_RECENT_ACTIVITIES_CACHE_KEY}|${JSON.stringify(variables)}`,
	).toString();

	const writeBackWithTTL = (
		value:
			| AggResponse<QuickFindRecentActivitiesQuery>
			| Promise<AggResponse<QuickFindRecentActivitiesQuery>>,
		ttl: number,
	) => {
		refreshOnAccessDecoratorRecentActivities({
			key: cacheKey,
			supplier: async () => value,
			returnExpired: false,
			onlyRefreshIfExpired: false,
			expiration: ttl,
		});
	};

	return refreshOnAccessDecoratorRecentActivities({
		key: cacheKey,
		supplier: async () => {
			try {
				const result = await fetchAgg<
					QuickFindRecentActivitiesQueryVariables,
					QuickFindRecentActivitiesQuery
				>({
					variables,
					aggAbsoluteUrl,
					graphQuery: quickFindRecentActivitiesQuery,
					operationName,
					headers,
				});
				writeBackWithTTL(result, SUCCESS_EXPIRATION_MS);
				return result;
			} catch (error) {
				// On failure, cache the rejected promise with a short TTL to avoid hammering the service
				// This ensures the error is still thrown when accessed, but prevents immediate retries
				writeBackWithTTL(Promise.reject(error), FAILURE_EXPIRATION_MS);
				// Re-throw the error so the caller knows the request failed
				throw error;
			}
		},
		expiration,
		onlyRefreshIfExpired,
		returnExpired,
		analyticsCallback,
		networkCallbacks,
	});
};

export const fetchJiraIssuesQuery: ({ variables, graphQuery, aggAbsoluteUrl, operationName, }: {
    variables: JiraIssuesQueryVariables;
    graphQuery: string;
    aggAbsoluteUrl: string | undefined;
    operationName: string;
}) => Promise<AggResponse<JiraIssueResponse>> = ({
	variables,
	graphQuery,
	aggAbsoluteUrl,
	operationName,
}: {
	variables: JiraIssuesQueryVariables;
	graphQuery: string;
	aggAbsoluteUrl: string | undefined;
	operationName: string;
}) => {
	return fetchAgg<JiraIssuesQueryVariables, JiraIssueResponse>({
		variables,
		aggAbsoluteUrl,
		graphQuery,
		operationName,
	});
};

export const __test_clearRecentActivitiesCache: (key?: string) => void = (key?: string): void => clearRecentActivitiesCache(key);
